// covered-by: frontends/carrie/src/routes/ask-puntt/components/__tests__/ChatMessage.test.tsx

/**
 * One message's Knowledge Database citations, in the two shapes it draws them.
 *
 * Beside the other citation helpers rather than inside `ChatMessage.tsx`: that
 * component was already well past the 400-line expert-legibility bar, and this
 * derivation is self-contained -- it reads a message's stored citations and
 * answers two questions about them, touching nothing else the component does.
 *
 * The two shapes are NOT interchangeable, which is the whole reason this is one
 * hook and not two calls at the call site:
 *
 * - `ruleCitations` is the deduped rule list. It is what the chips under the
 *   message are drawn and NUMBERED over.
 * - `claimLinks` is that same list as inline links in the prose, carrying the
 *   same numbers so a reader can tell which underlined phrase is which chip.
 *
 * Numbering and identity therefore come from the deduped list, while PROVENANCE
 * has to come from the RAW citations: `dedupeRuleCitations` drops every graph
 * record by design, and those records are what carry the chat or memory a claim
 * was discussed in. Deriving both here keeps that pairing in one place; reading
 * provenance off the deduped list was the first shape of this and every inline
 * link silently fell back to the registry.
 */

import { useMemo } from "react";

import {
  dedupeRuleCitations,
  ruleCitationKey,
  type TicketMessageCitation,
} from "./commentSourceUtils";
import {
  isRelatedCitation,
  useThreadCitationNumbersContext,
} from "./focusedCitedClaims";

import {
  chatsCitedInText,
  claimCitationsFor,
} from "../../../components/ask-puntt-review/claimCitations";
import { type LinkableClaim } from "../../../plugins/rehype-claim-citation";
import {
  type LinkableMemory,
  memoryLinksBySegment,
  memoryLinksFor,
} from "../../../utils/memoryCitationFields";

type RuleCitation = ReturnType<typeof dedupeRuleCitations>[number];

export interface MessageClaimCitations {
  /**
   * The rule citations, deduped on the visible quote, each with its number.
   * Filtered to the rule variant so only the fetch-free, Rules-tab-deep-linking
   * branch of `CommentCitationLinks` renders -- the file/record branches need
   * pane wiring and a follow-up fetch the chat message list does not have.
   * Deduped because different rules backed by the same evidence line would
   * otherwise render as duplicate chips. Sorted by number, so a source the
   * thread cited earlier leads with its lower number. Related rows are left
   * out: a chip is always a rule the answer cited.
   */
  ruleCitations: { citation: RuleCitation; n: number }[];
  /** The related rows left out of `ruleCitations`, counted, not numbered. */
  relatedCount: number;
  /**
   * The claims this answer QUOTES, as links in its own prose, carrying the
   * same numbers as the chips.
   */
  claimLinks: LinkableClaim[];
  /**
   * The saved memories this answer rests on that name the sentence they support,
   * numbered M1, M2 ... in order of first appearance. A memory with no
   * `supportText` (an older chat) is not here: it keeps the claim-wording link
   * and the rail row it always had. Empty unless the caller names the message.
   */
  memoryLinks: LinkableMemory[];
  /**
   * The memories each of the caller's segments draws, one array per segment in
   * the order they were handed over. Each memory is drawn by one text segment,
   * so its chip and testids appear once.
   */
  segmentMemoryLinks: LinkableMemory[][] | undefined;
}

const NO_MEMORY_LINKS: LinkableMemory[] = [];

/** Which message the memories belong to, and the reply their sentences are in. */
export interface MessageMemoryScope {
  /** The message or comment id: part of every chip's key. */
  scope: string;
  /** The reply text, so the memories are numbered in the order they appear. */
  content?: string;
  /**
   * The answer's text segments, when the caller renders it as several. A chip is
   * drawn by the one segment that holds the memory's sentence, so a sentence
   * straddling two segments is drawn in neither.
   */
  segments?: readonly (string | null)[];
}

/**
 * Inside a thread that provides `ThreadCitationNumbersContext`, a source's
 * number is the thread's, so the same source reads as the same number in every
 * answer and on the rail. Outside one, a message numbers its own citations
 * from 1.
 */
export function useMessageClaimCitations(
  citations: readonly TicketMessageCitation[] | undefined,
  memoryScope?: MessageMemoryScope,
): MessageClaimCitations {
  const threadNumbers = useThreadCitationNumbersContext();
  const deduped = useMemo(
    () => dedupeRuleCitations(citations ?? []),
    [citations],
  );

  const relatedCount = deduped.filter(isRelatedCitation).length;
  const ruleCitations = useMemo(() => {
    const numbered = deduped
      .filter((citation) => !isRelatedCitation(citation))
      .map((citation, index) => ({
        citation,
        n: threadNumbers?.get(ruleCitationKey(citation)) ?? index + 1,
      }));
    return threadNumbers ? numbered.sort((a, b) => a.n - b.n) : numbered;
  }, [deduped, threadNumbers]);

  const scope = memoryScope?.scope;
  const content = memoryScope?.content;
  const segments = memoryScope?.segments;
  const memoryLinks = useMemo(
    () => (scope ? memoryLinksFor(citations, content, scope) : NO_MEMORY_LINKS),
    [citations, content, scope],
  );
  // A memory whose sentence is found takes its chip there, so its claim wording
  // is not linked to it as well; one that is not found keeps that link.
  // why the segments as well as `inText`: the chip is drawn by the one segment
  // whose own text holds the sentence, so a sentence straddling two segments is
  // drawn in neither and `inText` (the whole reply) would still drop its link;
  // a one-body answer is handed every memory for the fallback, so there `inText`
  // is what says which of them the body holds.
  // why an approximation: `inText` matches the raw reply, the plugin the rendered blocks, with the same needles and fold; a miss only drops this inline link, never the rail card.
  // why one assignment: the chip a segment draws and the link `placedMemoryIds`
  // drops are one decision, so a caller that recomputed it could draw the memory twice.
  const segmentMemoryLinks = useMemo(
    () => (segments ? memoryLinksBySegment(segments, memoryLinks) : undefined),
    [memoryLinks, segments],
  );
  const placedMemoryIds = useMemo(() => {
    const assigned = segmentMemoryLinks
      ? new Set(segmentMemoryLinks.flat().map((memory) => memory.id))
      : undefined;
    return new Set(
      memoryLinks
        .filter(
          (memory) => memory.inText && (!assigned || assigned.has(memory.id)),
        )
        .map(({ id }) => id),
    );
  }, [memoryLinks, segmentMemoryLinks]);

  // The rail badges come from this same call on the message's own text, so a
  // conversation the answer does not print does not consume a ChatN here either.
  const chatNumbers = useMemo(
    () => chatsCitedInText(content, citations),
    [content, citations],
  );
  const claimLinks = useMemo(() => {
    const links = claimCitationsFor(
      { citations: deduped },
      citations ?? [],
      placedMemoryIds,
    );
    return links.map((link) => {
      const placed = link.chatId ? chatNumbers.get(link.chatId) : undefined;
      const n =
        threadNumbers && link.citation
          ? threadNumbers.get(ruleCitationKey(link.citation as RuleCitation))
          : undefined;
      if (placed == null && n == null) return link;
      return {
        ...link,
        ...(placed != null ? { chatNumber: placed } : {}),
        ...(n != null ? { number: n } : {}),
      };
    });
  }, [deduped, citations, threadNumbers, placedMemoryIds, chatNumbers]);

  return {
    ruleCitations,
    relatedCount,
    claimLinks,
    memoryLinks,
    segmentMemoryLinks,
  };
}
